import {
  announceToScreenReader,
  getVuejsDialogMessage,
} from '@/utils/screenReaderAnnounce';

const FOCUSABLE_SELECTOR = [
  "a[href]",
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

function isVisible(element) {
  if (!element || !element.isConnected) {
    return false;
  }
  const style = window.getComputedStyle(element);
  return (
    style.display !== "none" &&
    style.visibility !== "hidden" &&
    element.offsetParent !== null
  );
}

export function getFocusableElements(container) {
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute("disabled") && isVisible(el)
  );
}

export function trapFocus(container, previousFocus) {
  if (!container) {
    return () => {};
  }

  container.setAttribute("aria-modal", "true");
  if (!container.getAttribute("role")) {
    container.setAttribute("role", "dialog");
  }

  const focusFirstElement = () => {
    const focusable = getFocusableElements(container);
    if (focusable.length) {
      focusable[0].focus();
      return;
    }
    if (!container.hasAttribute("tabindex")) {
      container.setAttribute("tabindex", "-1");
    }
    container.focus();
  };

  const handleKeyDown = (event) => {
    if (event.key !== "Tab") {
      return;
    }

    const focusable = getFocusableElements(container);
    if (!focusable.length) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  container.addEventListener("keydown", handleKeyDown);
  requestAnimationFrame(() => {
    requestAnimationFrame(focusFirstElement);
  });

  return () => {
    container.removeEventListener("keydown", handleKeyDown);
    container.removeAttribute("aria-modal");
    if (container.getAttribute("role") === "dialog") {
      container.removeAttribute("role");
    }
    if (container.getAttribute("tabindex") === "-1") {
      container.removeAttribute("tabindex");
    }
    if (previousFocus && typeof previousFocus.focus === "function") {
      requestAnimationFrame(() => {
        previousFocus.focus();
      });
    }
  };
}

let activeRelease = null;
let trappedContainer = null;
let observer = null;
let scanScheduled = false;
let backgroundRoot = null;

function hideBackgroundFromAssistiveTech() {
  backgroundRoot = document.getElementById("app");
  if (backgroundRoot) {
    backgroundRoot.setAttribute("aria-hidden", "true");
  }
}

function showBackgroundToAssistiveTech() {
  if (backgroundRoot) {
    backgroundRoot.removeAttribute("aria-hidden");
    backgroundRoot = null;
  }
}

function releaseActiveTrap() {
  if (activeRelease) {
    activeRelease();
    activeRelease = null;
  }
  if (trappedContainer) {
    delete trappedContainer.dataset.focusTrapActive;
    trappedContainer.removeAttribute('aria-label');
    trappedContainer = null;
  }
  showBackgroundToAssistiveTech();
}

function activateTrap(container) {
  if (!container || container.dataset.focusTrapActive === "true") {
    return;
  }

  releaseActiveTrap();
  const previousFocus = document.activeElement;
  container.dataset.focusTrapActive = "true";
  trappedContainer = container;
  hideBackgroundFromAssistiveTech();

  const dialogMessage = getVuejsDialogMessage(container);
  if (dialogMessage) {
    container.setAttribute('aria-label', dialogMessage);
    announceToScreenReader(dialogMessage);
  }

  activeRelease = trapFocus(container, previousFocus);
}

function scanForDialog() {
  const container = document.querySelector(".dg-container");

  if (container && isVisible(container)) {
    if (container !== trappedContainer) {
      activateTrap(container);
    }
    return;
  }

  releaseActiveTrap();
}

function scheduleDialogScan() {
  if (scanScheduled) {
    return;
  }
  scanScheduled = true;
  requestAnimationFrame(() => {
    scanScheduled = false;
    scanForDialog();
  });
}

export function initVuejsDialogFocusTrap() {
  if (typeof document === "undefined" || observer) {
    return;
  }

  observer = new MutationObserver(() => {
    scheduleDialogScan();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["class", "style", "aria-hidden"],
  });

  scanForDialog();
}

export function destroyVuejsDialogFocusTrap() {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
  releaseActiveTrap();
}



// WEBPACK FOOTER //
// ./src/utils/focusTrap.js